<!-- ============================================ -->
<!-- SECTION 1: HERO — Full-bleed gradient with floating orbs -->
<!-- ============================================ -->
<section class="hero-section">
  <!-- Decorative background orbs -->
  <div class="hero-orb hero-orb-1"></div>
  <div class="hero-orb hero-orb-2"></div>
  <div class="hero-orb hero-orb-3"></div>

  <!-- Infographic background layer -->
  <div class="hero-infographic" aria-hidden="true">
    <!-- Subtle grid -->
    <svg
      class="infographic-grid"
      viewBox="0 0 1440 900"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <pattern
          id="heroGrid"
          x="0"
          y="0"
          width="80"
          height="80"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M 80 0 L 0 0 0 80"
            fill="none"
            stroke="rgba(24,70,125,0.09)"
            stroke-width="1"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#heroGrid)" />
    </svg>

    <!-- Left cluster: Portfolio pie chart -->
    <div class="infographic-element ig-pie-chart">
      <svg viewBox="0 0 120 120">
        <circle
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke="rgba(24,70,125,0.12)"
          stroke-width="13"
        />
        <circle
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke="rgba(24,70,125,0.55)"
          stroke-width="13"
          stroke-dasharray="82 245"
          stroke-dashoffset="0"
          stroke-linecap="round"
        />
        <circle
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke="rgba(86,197,150,0.65)"
          stroke-width="13"
          stroke-dasharray="65 262"
          stroke-dashoffset="-82"
          stroke-linecap="round"
        />
        <circle
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke="rgba(42,108,184,0.40)"
          stroke-width="13"
          stroke-dasharray="50 277"
          stroke-dashoffset="-147"
          stroke-linecap="round"
        />
        <text
          x="60"
          y="56"
          text-anchor="middle"
          fill="rgba(24,70,125,0.75)"
          font-size="11"
          font-weight="700"
          font-family="Inter,sans-serif"
        >
          AUM
        </text>
        <text
          x="60"
          y="72"
          text-anchor="middle"
          fill="rgba(24,70,125,0.55)"
          font-size="9"
          font-family="Inter,sans-serif"
        >
          ₹2.4Cr
        </text>
      </svg>
    </div>

    <!-- Bottom-right: Rising bar chart -->
    <div class="infographic-element ig-bar-chart">
      <svg viewBox="0 0 160 100">
        <rect
          x="10"
          y="60"
          width="18"
          height="35"
          rx="3"
          fill="rgba(24,70,125,0.20)"
        />
        <rect
          x="38"
          y="45"
          width="18"
          height="50"
          rx="3"
          fill="rgba(24,70,125,0.30)"
        />
        <rect
          x="66"
          y="30"
          width="18"
          height="65"
          rx="3"
          fill="rgba(24,70,125,0.40)"
        />
        <rect
          x="94"
          y="18"
          width="18"
          height="77"
          rx="3"
          fill="rgba(42,108,184,0.50)"
        />
        <rect
          x="122"
          y="8"
          width="18"
          height="87"
          rx="3"
          fill="rgba(86,197,150,0.60)"
        />
        <line
          x1="5"
          y1="95"
          x2="150"
          y2="95"
          stroke="rgba(24,70,125,0.25)"
          stroke-width="1.5"
        />
        <!-- Trend arrow -->
        <polyline
          points="19,55 47,40 75,25 103,13 131,3"
          fill="none"
          stroke="rgba(24,70,125,0.45)"
          stroke-width="2"
          stroke-dasharray="4 3"
        />
        <polygon points="131,3 126,10 135,8" fill="rgba(24,70,125,0.45)" />
      </svg>
    </div>

    <!-- Top-right: SIP flow nodes -->
    <div class="infographic-element ig-flow-nodes">
      <svg viewBox="0 0 200 80">
        <!-- Node 1: Investor -->
        <rect
          x="2"
          y="22"
          width="50"
          height="36"
          rx="8"
          fill="rgba(24,70,125,0.12)"
          stroke="rgba(24,70,125,0.35)"
          stroke-width="1.2"
        />
        <text
          x="27"
          y="38"
          text-anchor="middle"
          fill="rgba(24,70,125,0.85)"
          font-size="7"
          font-weight="700"
          font-family="Inter,sans-serif"
        >
          SIP
        </text>
        <text
          x="27"
          y="49"
          text-anchor="middle"
          fill="rgba(24,70,125,0.60)"
          font-size="5.5"
          font-family="Inter,sans-serif"
        >
          Investor
        </text>
        <!-- Arrow -->
        <line
          x1="54"
          y1="40"
          x2="72"
          y2="40"
          stroke="rgba(24,70,125,0.40)"
          stroke-width="1.5"
        />
        <polygon points="72,40 67,37 67,43" fill="rgba(24,70,125,0.40)" />
        <!-- Node 2: AMC -->
        <rect
          x="75"
          y="22"
          width="50"
          height="36"
          rx="8"
          fill="rgba(86,197,150,0.18)"
          stroke="rgba(86,197,150,0.50)"
          stroke-width="1.2"
        />
        <text
          x="100"
          y="38"
          text-anchor="middle"
          fill="rgba(10,80,40,0.85)"
          font-size="7"
          font-weight="700"
          font-family="Inter,sans-serif"
        >
          AMC
        </text>
        <text
          x="100"
          y="49"
          text-anchor="middle"
          fill="rgba(10,80,40,0.55)"
          font-size="5.5"
          font-family="Inter,sans-serif"
        >
          Process
        </text>
        <!-- Arrow -->
        <line
          x1="127"
          y1="40"
          x2="145"
          y2="40"
          stroke="rgba(24,70,125,0.40)"
          stroke-width="1.5"
        />
        <polygon points="145,40 140,37 140,43" fill="rgba(24,70,125,0.40)" />
        <!-- Node 3: NAV -->
        <rect
          x="148"
          y="22"
          width="50"
          height="36"
          rx="8"
          fill="rgba(42,108,184,0.14)"
          stroke="rgba(42,108,184,0.40)"
          stroke-width="1.2"
        />
        <text
          x="173"
          y="38"
          text-anchor="middle"
          fill="rgba(24,70,125,0.85)"
          font-size="7"
          font-weight="700"
          font-family="Inter,sans-serif"
        >
          NAV
        </text>
        <text
          x="173"
          y="49"
          text-anchor="middle"
          fill="rgba(24,70,125,0.60)"
          font-size="5.5"
          font-family="Inter,sans-serif"
        >
          Allotment
        </text>
      </svg>
    </div>

    <!-- Bottom-right: Line chart (NAV trend) -->
    <div class="infographic-element ig-line-chart">
      <svg viewBox="0 0 180 90">
        <defs>
          <linearGradient id="navFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="rgba(24,70,125,0.28)" />
            <stop offset="100%" stop-color="rgba(24,70,125,0.02)" />
          </linearGradient>
        </defs>
        <!-- Axis lines -->
        <line
          x1="10"
          y1="10"
          x2="10"
          y2="82"
          stroke="rgba(24,70,125,0.20)"
          stroke-width="1"
        />
        <line
          x1="10"
          y1="82"
          x2="175"
          y2="82"
          stroke="rgba(24,70,125,0.20)"
          stroke-width="1"
        />
        <path
          d="M10,70 Q30,65 50,55 T90,35 T130,25 T170,15"
          fill="none"
          stroke="rgba(24,70,125,0.60)"
          stroke-width="2.5"
          stroke-linecap="round"
        />
        <path
          d="M10,70 Q30,65 50,55 T90,35 T130,25 T170,15 L170,82 L10,82 Z"
          fill="url(#navFill)"
        />
        <circle
          cx="170"
          cy="15"
          r="4"
          fill="rgba(86,197,150,0.80)"
          stroke="rgba(86,197,150,1)"
          stroke-width="1.5"
        />
        <circle
          cx="90"
          cy="35"
          r="3"
          fill="rgba(24,70,125,0.40)"
          stroke="rgba(24,70,125,0.60)"
          stroke-width="1"
        />
        <text
          x="12"
          y="89"
          fill="rgba(24,70,125,0.50)"
          font-size="6"
          font-family="Inter,sans-serif"
        >
          Jan
        </text>
        <text
          x="82"
          y="89"
          fill="rgba(24,70,125,0.50)"
          font-size="6"
          font-family="Inter,sans-serif"
        >
          Jun
        </text>
        <text
          x="155"
          y="89"
          fill="rgba(24,70,125,0.50)"
          font-size="6"
          font-family="Inter,sans-serif"
        >
          Dec
        </text>
      </svg>
    </div>

    <!-- Floating data points -->
    <div class="infographic-element ig-data-dot ig-dot-1"></div>
    <div class="infographic-element ig-data-dot ig-dot-2"></div>
    <div class="infographic-element ig-data-dot ig-dot-3"></div>

    <!-- Connecting lines pattern -->
    <svg
      class="infographic-connections"
      viewBox="0 0 1440 900"
      preserveAspectRatio="xMidYMid slice"
    >
      <line
        x1="220"
        y1="350"
        x2="380"
        y2="280"
        stroke="rgba(24,70,125,0.14)"
        stroke-width="1.2"
        stroke-dasharray="6 4"
      />
      <line
        x1="1060"
        y1="200"
        x2="1200"
        y2="350"
        stroke="rgba(24,70,125,0.14)"
        stroke-width="1.2"
        stroke-dasharray="6 4"
      />
      <line
        x1="300"
        y1="650"
        x2="500"
        y2="550"
        stroke="rgba(24,70,125,0.10)"
        stroke-width="1"
        stroke-dasharray="6 4"
      />
      <line
        x1="950"
        y1="600"
        x2="1150"
        y2="500"
        stroke="rgba(24,70,125,0.10)"
        stroke-width="1"
        stroke-dasharray="6 4"
      />
      <circle cx="380" cy="280" r="3" fill="rgba(24,70,125,0.22)" />
      <circle cx="1060" cy="200" r="3" fill="rgba(24,70,125,0.22)" />
      <circle cx="500" cy="550" r="3" fill="rgba(86,197,150,0.35)" />
    </svg>
  </div>

  <div class="hero-inner">
    <div class="hero-content" [class.visible]="heroVisible">
      <span class="hero-badge">
        <span class="badge-pulse"></span>
        <i class="pi pi-verified" aria-hidden="true"></i> Trusted by Brokers
        &amp; Wealth Managers
      </span>
      <h1 class="hero-headline">
        Smarter Way to Manage
        <span class="hero-gradient-text">Mutual Fund Operations</span>
      </h1>
      <p class="hero-sub">Go Live in 24 Hours with Gemfin.</p>
      <div class="hero-ctas">
        <button class="btn-primary btn-glow" (click)="openGetInTouchModal()">
          <i class="pi pi-envelope" aria-hidden="true"></i> Get in Touch
          <span class="btn-shine"></span>
        </button>
      </div>
    </div>
  </div>
  <div class="hero-wave">
    <svg viewBox="0 0 1440 100" preserveAspectRatio="none">
      <path
        d="M0,40 C360,100 1080,0 1440,60 L1440,100 L0,100 Z"
        fill="#ffffff"
      />
    </svg>
  </div>
</section>

<!-- ============================================ -->
<!-- SECTION 3: VIDEO + FEATURES (2-column) -->
<!-- ============================================ -->
<section class="showcase-section" #psSection>
  <div class="section-header" [class.visible]="psVisible">
    <span class="section-tag-pill">Features</span>
    <h2 class="section-title">
      Everything You Need to <span class="gradient-text">Grow</span>
    </h2>
  </div>
  <div class="showcase-inner" [class.visible]="psVisible">
    <!-- LEFT: Video -->
    <div class="showcase-video-col">
      <div class="video-wrapper">
        <video
          class="showcase-video"
          poster="../../../assets/images/gemfin_logo.svg"
          controls
          preload="metadata"
        >
          <source src="" type="video/mp4" />
        </video>
        <div class="video-overlay">
          <div class="video-play-btn">
            <i class="pi pi-play" aria-hidden="true"></i>
          </div>
          <span class="video-caption">Watch GemFin in Action</span>
        </div>
      </div>
    </div>

    <!-- RIGHT: Consolidated Features -->
    <div class="showcase-features-col" id="features" #featuresSection>
      <div class="feature-list">
        @for (item of consolidatedFeatures; track item.title; let i = $index) {
          <div class="fl-item" [style.animation-delay]="i * 0.05 + 's'">
            <div class="fl-icon" [ngClass]="item.cls">
              <i [class]="item.icon" aria-hidden="true"></i>
            </div>
            <div class="fl-body">
              <h4>{{ item.title }}</h4>
              <p>{{ item.desc }}</p>
            </div>
          </div>
        }
      </div>
    </div>
  </div>
</section>

<!-- ============================================ -->
<!-- SECTION 8: WORKFLOW — Animated step timeline -->
<!-- ============================================ -->
<section class="workflow-section" #workflowSection>
  <div class="section-header" [class.visible]="workflowVisible">
    <span class="section-tag-pill">How It Works</span>
    <h2 class="section-title">
      Get Started in <span class="gradient-text">5 Simple Steps</span>
    </h2>
  </div>
  <div class="timeline" [class.visible]="workflowVisible">
    <!-- Progress line behind steps -->
    <div class="timeline-track">
      <div
        class="timeline-track-fill"
        [style.width]="workflowVisible ? '100%' : '0%'"
      ></div>
    </div>
    @for (step of workflowSteps; track step.step; let i = $index) {
      <div class="timeline-step" [style.animation-delay]="i * 0.2 + 's'">
        <div class="timeline-number">
          <span>{{ step.step }}</span>
        </div>
        <h3>{{ step.title }}</h3>
        <p>{{ step.desc }}</p>
      </div>
    }
  </div>
</section>

<app-get-in-touch-form #getInTouchFormDialog></app-get-in-touch-form>
